import { useState } from "react";
import {
    TextField,
    Filter,
    TextInput,
    usePermissions,
    FunctionField,
    useTranslate,
} from "react-admin";
import { DefaultPagination } from "shared/src/components/DefaultPagination";
import { ToolbarWithHistory } from "shared/src/components/ToolbarWithHistory";
import { DatagridWithHistory } from "shared/src/components/DatagridWithHistory";
import { queryToAttribute } from "shared/src/utils/decodeQuery";
import { AccessRightsError } from "shared/src/components/AccessRightsError";
import { RaSelectInput } from "shared/src/components/inputs/Ra/RaSelectInput";
import { RaDateField } from "shared/src/components/fields/Ra/RaDateField";
import { RaListView } from "shared/src/components/views/RaListView";
import { TextField as MuiTextField } from "@mui/material";
import { handleHttpError } from "shared/src/utils/errorHandler";
import { AutocompleteInput } from "shared/src/components/inputs/AutocompleteInput";

const ListFilter = ({ renter, setRenter, ...props }) => {
    const translate = useTranslate();

    return (
        <Filter {...props}>
            <TextInput
                title={translate("equip.name")}
                label="equip.name"
                source="search"
                alwaysOn
            />
            <RaSelectInput
                label="equip.dunning.level"
                source="dunningLevel"
                choices={[
                    { id: 0, name: "equip.dunning.level_0" },
                    { id: 1, name: "equip.dunning.level_1" },
                    { id: 2, name: "equip.dunning.level_2" },
                    { id: 3, name: "equip.dunning.level_3" },
                ]}
                alwaysOn
            />
            <AutocompleteRenterInput
                value={renter}
                setValue={setRenter}
                alwaysOn
            />
        </Filter>
    );
};

export const OverdueRentalsList = (props) => {
    const translate = useTranslate();
    const attribute = queryToAttribute();
    const { isLoading, permissions } = usePermissions();
    const [renter, setRenter] = useState("");

    if (isLoading) {
        return <></>;
    } else if (!permissions.vote({ resource: 'rentals', attribute })) {
        return <AccessRightsError />;
    }

    return (
        <RaListView
            {...props}
            resource={"rentals"}
            actions={<ToolbarWithHistory export={true} />}
            filters={<ListFilter setRenter={setRenter} />}
            pagination={<DefaultPagination />}
            perPage={50}
            sort={{ field: "id", order: "DESC" }}
            filter={{ renter: renter?.id, overdueRentals: true }}
            {...(renter && { empty: false })}
        >
            <DatagridWithHistory bulkActionButtons={false}>
                <FunctionField
                    label="ID"
                    source="id"
                    render={(record) =>
                        record.edispoId
                            ? `${record.id} / ${record.edispoId}`
                            : record.id
                    }
                />
                <TextField
                    label="equip.name"
                    source="name"
                    sortable={false}
                />
                <TextField
                    label="equip.renter"
                    source="actualRentalProcess.renter.fullName"
                    sortable={false}
                />
                <FunctionField
                    label="equip.openItems"
                    render={(record) => record.openItemCount + ' / ' + record.itemCount}
                    sortable={false}
                />
                <FunctionField
                    label="equip.state"
                    source="state"
                    render={(record) => translate("equip." + record.state)}
                    sortable={false}
                />
                <RaDateField
                    label="equip.startDate"
                    source="startDate"
                    sortable={false}
                />
                <RaDateField
                    label="equip.endDate"
                    source="endDate"
                    sortable={false}
                />
                <RaDateField
                    label="equip.signatureDate"
                    source="actualRentalProcess.signatureDate"
                    sortable={false}
                />
                <FunctionField
                    label="equip.dunning.label"
                    render={(record) => {
                        let result = "";

                        if (record.daysOverdue > 0) {
                            result += `${record.daysOverdue} ${translate(
                                "equip." +
                                    (record.daysOverdue === 1 ? "day" : "days")
                            )}`;
                        } else if (null !== record.dunning) {
                            result += translate(
                                "equip.dunning.level_" + record.dunning.level
                            );
                        }

                        if (
                            null !== record.dunning &&
                            null !== record.daysOverdueDunning
                        ) {
                            result += ` (${translate(
                                "equip.dunning.level_" + record.dunning.level
                            )} ${record.daysOverdueDunning} ${translate(
                                "equip." +
                                    (record.daysOverdueDunning === 1
                                        ? "day"
                                        : "days")
                            )})`;
                        }

                        return result;
                    }}
                    sortable={false}
                />
            </DatagridWithHistory>
        </RaListView>
    );
};

const AutocompleteRenterInput = ({ value, setValue }) => {
    return (
        <AutocompleteInput
            value={value}
            setValue={setValue}
            resource="renters"
            labelKey="equip.renter"
            sort={{ field: "lastname", order: "ASC" }}
            filterBy={"search"}
            renderOptionLabel={({ fullName, userIdentifier }) => {
                return `${fullName} (${userIdentifier})`;
            }}
            RenderInputComponent={MuiTextField}
            handleError={handleHttpError}
            sx={{ width: 400 }}
        />
    );
};
